<template>
  <div v-loading="loading">

    <!-- GET error -->
    <el-alert
      v-if="loadError"
      type="error"
      :closable="false"
      show-icon
      style="margin-bottom:16px;">
      Couldn't load the tier template. Close and try again.
    </el-alert>

    <!-- Info banner -->
    <el-alert
      v-else
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom:16px;">
      These tier month ranges are the starting point for every new sharer rate. Rate values are entered per rate, not here.
    </el-alert>

    <!-- Tier editor (only when no hard error) -->
    <sharer-rate-tiers
      v-if="!loadError"
      ref="tiersEditor"
      v-model="tiers"
      template-mode
      :disabled="false">
    </sharer-rate-tiers>

  </div>
</template>

<script>
import axios from '@/lib/axiosClient'
import SiteMixins from '@/mixins/SiteMixins'
import SharerRateTiers from './SharerRateTiers'
import {DISABLE_SAVE_EVENT, ENABLE_SAVE_EVENT} from '@/components/Data'

// One blank template tier for the "no saved template" state.
function blankTemplateTier () {
  return {
    seqNo: 1,
    fromMonth: 0,
    toMonth: 12,
    telco1: null,
    telco2: null,
    telco3: null,
    telco4: null,
    telco5: null,
    telco6: null,
    telco7: null,
    telco8: null,
    telco9: null,
    telco10: null,
    mwRate: null,
    rruRate: null,
    rruSet: null
  }
}

export default {
  name: 'default-tier-template-drawer',

  mixins: [SiteMixins],

  components: {
    SharerRateTiers
  },

  data () {
    return {
      tiers: [],
      loading: false,
      loadError: false
    }
  },

  watch: {
    tiers: {
      deep: true,
      handler: function () {
        this.syncSaveButton()
      }
    }
  },

  methods: {
    // Called by DefaultTierTemplateButton on each open.
    load () {
      this.loadError = false
      this.loading = true
      this.$events.emit(DISABLE_SAVE_EVENT)
      this.fetchData(
        '/rates/sharer/default-tiers',
        this.onTemplateLoaded,
        'tier template',
        this.onLoadError
      )
    },

    onTemplateLoaded (data) {
      this.loading = false
      if (data && data.tiers && Array.isArray(data.tiers) && data.tiers.length > 0) {
        // Map API tiers — preserve null rate values (don't coerce to 0)
        this.tiers = data.tiers
          .slice()
          .sort(function (a, b) { return (a.seqNo || 0) - (b.seqNo || 0) })
          .map(function (t, i) {
            return {
              seqNo: i + 1,
              fromMonth: Number(t.fromMonth) || 0,
              toMonth: Number(t.toMonth) || 0,
              telco1: t.telco1 !== null && t.telco1 !== undefined ? Number(t.telco1) : null,
              telco2: t.telco2 !== null && t.telco2 !== undefined ? Number(t.telco2) : null,
              telco3: t.telco3 !== null && t.telco3 !== undefined ? Number(t.telco3) : null,
              telco4: t.telco4 !== null && t.telco4 !== undefined ? Number(t.telco4) : null,
              telco5: t.telco5 !== null && t.telco5 !== undefined ? Number(t.telco5) : null,
              telco6: t.telco6 !== null && t.telco6 !== undefined ? Number(t.telco6) : null,
              telco7: t.telco7 !== null && t.telco7 !== undefined ? Number(t.telco7) : null,
              telco8: t.telco8 !== null && t.telco8 !== undefined ? Number(t.telco8) : null,
              telco9: t.telco9 !== null && t.telco9 !== undefined ? Number(t.telco9) : null,
              telco10: t.telco10 !== null && t.telco10 !== undefined ? Number(t.telco10) : null,
              mwRate: t.mwRate !== null && t.mwRate !== undefined ? Number(t.mwRate) : null,
              rruRate: t.rruRate !== null && t.rruRate !== undefined ? Number(t.rruRate) : null,
              rruSet: t.rruSet !== null && t.rruSet !== undefined ? Number(t.rruSet) : null
            }
          })
      } else {
        // data is null — no template saved yet; seed one blank tier
        this.tiers = [blankTemplateTier()]
      }
      this.syncSaveButton()
    },

    onLoadError (err) {
      this.loading = false
      this.loadError = true
      this.$events.emit(DISABLE_SAVE_EVENT)
      console.error('[DefaultTierTemplateDrawer] GET failed', err)
    },

    syncSaveButton () {
      var self = this
      // Use $nextTick so $refs.tiersEditor is populated after tiers render.
      this.$nextTick(function () {
        var valid = !self.loading &&
          !self.loadError &&
          self.$refs.tiersEditor &&
          self.$refs.tiersEditor.validate()
        if (valid) {
          self.$events.emit(ENABLE_SAVE_EVENT)
        } else {
          self.$events.emit(DISABLE_SAVE_EVENT)
        }
      })
    },

    // Serialize the current tiers for the PUT payload.
    // Months → Number; rate fields → null when blank/NaN, else Number.
    serializeTiers () {
      return this.tiers.map(function (t, i) {
        function rateVal (v) {
          if (v === null || v === undefined || v === '') return null
          var n = Number(v)
          return isNaN(n) ? null : n
        }
        return {
          seqNo: i + 1,
          fromMonth: Number(t.fromMonth),
          toMonth: Number(t.toMonth),
          telco1: rateVal(t.telco1),
          telco2: rateVal(t.telco2),
          telco3: rateVal(t.telco3),
          telco4: rateVal(t.telco4),
          telco5: rateVal(t.telco5),
          telco6: rateVal(t.telco6),
          telco7: rateVal(t.telco7),
          telco8: rateVal(t.telco8),
          telco9: rateVal(t.telco9),
          telco10: rateVal(t.telco10),
          mwRate: rateVal(t.mwRate),
          rruRate: rateVal(t.rruRate),
          rruSet: rateVal(t.rruSet)
        }
      })
    },

    // Called by DefaultTierTemplateButton on @save.
    // Returns a promise so the button can close the drawer on success.
    //
    // NOTE: Popup's @save fires after the drawer is hidden (`:visible.sync`). The
    // ref guard below is belt-and-suspenders — Popup does NOT destroy-on-close so
    // $refs.tiersEditor should still exist, but guard anyway in case that changes.
    // The serialized payload is captured from this.tiers (data property) rather
    // than from the ref, so serialization is safe even if the ref were stale.
    save () {
      // Guard: ref may be absent if Popup ever gains destroy-on-close.
      if (!this.$refs.tiersEditor) {
        return Promise.reject(new Error('tiersEditor ref unavailable'))
      }
      if (!this.$refs.tiersEditor.validate()) {
        return Promise.reject(new Error('invalid'))
      }

      var self = this
      var payload = { tiers: this.serializeTiers() }

      return new Promise(function (resolve, reject) {
        self.send(
          self,                                               // 1: self
          self.getEndpoint('/rates/sharer/default-tiers', null, false), // 2: url
          payload,                                           // 3: formData
          'tier template',                                   // 4: item
          function () {                                      // 5: successCallback
            self.$message({ type: 'success', message: 'Tier template saved.' })
            resolve()
          },
          function (err) {                                   // 6: failCallback
            // send() already toasted via notifyFailureCallback; add a fallback
            // message only for network errors where err.response is absent.
            if (!err || !err.response) {
              self.$message({ type: 'error', message: "Couldn't save the tier template. Please try again." })
            }
            reject(err)
          },
          axios.put,                                         // 7: formAction
          false,                                             // 8: showMessage
          'Save',                                            // 9: actionType
          null,                                              // 10: tableRef
          true                                               // 11: notifyFailureCallback
        )
      })
    }
  }
}
</script>
